import { useEffect, useState } from 'react';
import { X, Download, ZoomIn, ZoomOut, Loader2, Share2, AlertCircle } from 'lucide-react';
import api from '../../api/client';
import { Modal } from '../common/Modal';
import type { FileItem } from '../../types';

interface FilePreviewProps {
  file: FileItem;
  onClose: () => void;
  onShare?: () => void;
}

export default function FilePreview({ file, onClose, onShare }: FilePreviewProps) {
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isImage = file.mime_type.startsWith('image/');
  const isPDF = file.mime_type === 'application/pdf';

  useEffect(() => {
    if (!isImage && !isPDF) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let currentObjectUrl: string | null = null;

    const fetchPreview = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`/api/v1/files/${file.id}/view`, {
          responseType: 'blob',
        });

        if (cancelled) {
          return;
        }

        currentObjectUrl = URL.createObjectURL(response.data);
        setObjectUrl(currentObjectUrl);
      } catch {
        if (!cancelled) {
          setError('Önizleme yüklenirken bir hata oluştu.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [file.id, file.mime_type, isImage, isPDF]);

  const handleDownload = async () => {
    try {
      const response = await api.get(`/api/v1/files/${file.id}/download`, {
        responseType: 'blob',
      });

      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Dosya indirilirken bir hata oluştu.');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      ariaLabel={file.original_name}
      zIndex={100}
      overlayClassName="bg-black/90 p-0"
      panelUnstyled
      panelClassName="flex h-full w-full flex-col"
    >
      {/* Üst bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-white">{file.original_name}</p>
          <p className="text-xs text-slate-400">
            {(file.size / 1024).toFixed(1)} KB • {file.mime_type}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isImage && (
            <>
              <button
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                className="rounded-lg p-2 text-white hover:bg-white/10"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <button
                onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                className="rounded-lg p-2 text-white hover:bg-white/10"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
            </>
          )}
          <button
            data-testid="preview-download-button"
            onClick={handleDownload}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Download className="h-4 w-4" />
            İndir
          </button>
          {onShare && (
            <button
              onClick={onShare}
              data-testid="preview-share-button"
              className="flex items-center gap-1.5 rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/20"
            >
              <Share2 className="h-4 w-4" />
              Paylaş
            </button>
          )}
          <button data-testid="preview-close-button" onClick={onClose} className="rounded-lg p-2 text-white hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* İçerik */}
      <div className="relative flex flex-1 items-center justify-center overflow-auto p-4">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-xl bg-red-500/10 px-6 py-4 text-center text-red-200">
              <AlertCircle className="mx-auto mb-2 h-8 w-8" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {isImage && objectUrl && !error && (
          <img
            src={objectUrl}
            alt={file.original_name}
            className="max-h-[90vh] max-w-full object-contain transition-transform"
            style={{ transform: `scale(${zoom})` }}
            onLoad={() => setLoading(false)}
          />
        )}

        {isPDF && objectUrl && !error && (
          <iframe
            src={objectUrl}
            title={file.original_name}
            className="h-full w-full max-w-full rounded-lg bg-white md:max-w-4xl"
            onLoad={() => setLoading(false)}
          />
        )}

        {!isImage && !isPDF && (
          <div className="text-center">
            <p className="mb-3 text-white">Bu dosya türü önizleme desteklemiyor.</p>
            <button
              data-testid="preview-download-unsupported-button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Download className="h-4 w-4" />
              İndir
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
